import { forwardRef, memo, useMemo, useState } from "react"
import { useDroppable } from "@dnd-kit/core"
import { SortableContext, useSortable } from "@dnd-kit/sortable"
import { CSS } from "@dnd-kit/utilities"
import { useNavigate, useParams } from "@tanstack/react-router"
import {
	IconCheckOutline18 as Check,
	IconDotsOutline18 as Dots,
	IconMoveToRightOutline18 as MoveToPortal,
	IconPenOutline18 as PenIcon,
	IconPlusOutline18 as Plus,
	IconArrowTurnRightOutline18 as RedirectIcon,
	IconTabsOutline18 as TabsIcon,
	IconTrashOutline18 as Trash,
} from "nucleo-ui-outline-18"

import { cn } from "@productlane/lib/src/cn"
import { isLooseGroup } from "@productlane/lib/src/help-center/loose-group"
import { AlertDialog, Badge, IconButton, Tooltip } from "@productlane/ui"
import {
	DropdownMenu,
	DropdownMenuItem,
	DropdownMenuPortal,
	DropdownMenuSub,
	DropdownMenuSubContent,
	DropdownMenuSubTrigger,
} from "@productlane/ui/src/components/internal/dropdown"
import { mutators } from "@productlane/zero/mutators"

import type { GroupHeaderDropTarget, GroupWithArticles } from "./types"
import { toast } from "@/components/toast"
import { analytics } from "@/lib/analytics"
import { generateId } from "@/lib/id"
import { useRole } from "@/lib/useRole"
import { mutateOk, useZero, useZeroWorkspace } from "@/lib/zero"
import { getActivePortalInstanceId } from "@/stores/active-portal-instance-store"
import { useDocNavStore } from "@/stores/docs/docs-nav-store"
import { folderRedirects } from "./folder-redirects"
import { FolderRedirectsDialog } from "./folder-redirects-dialog"
import { GroupNameInput } from "./group-name-input"
import { useHelpCenterNav } from "./help-center-context"
import { useDropSignal } from "./help-center-drop-indicator"
import { HelpCenterNavItem } from "./help-center-nav-item"
import { MoveToPortalDialog } from "./move-to-portal-dialog"
import { NavDisclosureChevron } from "./nav-disclosure-chevron"
import { NavRowActions } from "./nav-row-actions"
import { noSortShift } from "./no-sort-shift"
import { renameGroup } from "./rename-group"
import { RenameGroupDialog } from "./rename-group-dialog"
import { translatedName } from "./translated-title"
import { groupHeaderDropId } from "./types"
import { useDelayedUnmount } from "./use-delayed-unmount"
import { groupTabId, useDocsTabs } from "./use-docs-tabs"

interface HelpCenterNavGroupProps {
	group: GroupWithArticles
	selectedItemRef?: React.RefObject<HTMLLIElement | null>
}

export const HelpCenterNavGroup = memo(
	forwardRef<HTMLDivElement, HelpCenterNavGroupProps>((props, ref) =>
		isLooseGroup(props.group) ? (
			<LooseNavGroup {...props} ref={ref} />
		) : (
			<NamedNavGroup {...props} ref={ref} />
		),
	),
)

// Top-level pages: no header, so the rows sit in the tree beside the folders.
// The group stays a sortable node only so a dragged folder can land next to it.
const LooseNavGroup = forwardRef<HTMLDivElement, HelpCenterNavGroupProps>(
	({ group, selectedItemRef }, ref) => {
		const params = useParams({ strict: false })
		const articleIds = useMemo(
			() => group.helpCenterArticles.map((a) => a.id),
			[group.helpCenterArticles],
		)
		const sortableData = useMemo(
			() => ({ type: "group" as const, group }),
			[group],
		)
		const { setNodeRef, transform, transition } = useSortable({
			id: group.id,
			data: sortableData,
			disabled: { draggable: true, droppable: false },
		})
		const dropSide = useDropSignal(group.id)

		if (group.helpCenterArticles.length === 0) return null

		return (
			<div
				ref={(el) => {
					setNodeRef(el)
					if (typeof ref === "function") ref(el)
					else if (ref) ref.current = el
				}}
				style={{ transform: CSS.Translate.toString(transform), transition }}
				className="relative"
			>
				{dropSide === "before" && (
					<div className="pointer-events-none absolute inset-x-1 -top-0.5 z-20 h-0.5 rounded-full bg-black dark:bg-white" />
				)}
				{dropSide === "after" && (
					<div className="pointer-events-none absolute inset-x-1 -bottom-0.5 z-20 h-0.5 rounded-full bg-black dark:bg-white" />
				)}
				<ul className="list-none space-y-0.5 pb-3 text-secondary">
					<SortableContext items={articleIds} strategy={noSortShift}>
						{group.helpCenterArticles.map((item) => (
							<HelpCenterNavItem
								key={item.id}
								article={item}
								groupId={group.id}
								sortableDisabled={false}
								ref={item.id === params.id ? selectedItemRef : undefined}
							/>
						))}
					</SortableContext>
				</ul>
			</div>
		)
	},
)

const NamedNavGroup = memo(
	forwardRef<HTMLDivElement, HelpCenterNavGroupProps>(
		({ group, selectedItemRef }, ref) => {
			const toggleGroup = useDocNavStore((s) => s.toggleGroup)
			const openGroup = useDocNavStore((s) => s.openGroup)
			const isOpen = useDocNavStore((s) => s.openGroupIds.includes(group.id))
			const [isRenameDialogOpen, setIsRenameDialogOpen] = useState(false)
			const [isDeleteOpen, setIsDeleteOpen] = useState(false)
			const [isMoveOpen, setIsMoveOpen] = useState(false)
			const [isRedirectsOpen, setIsRedirectsOpen] = useState(false)
			const [isEditingName, setIsEditingName] = useState(false)
			const zero = useZero()
			const workspace = useZeroWorkspace()
			const { role } = useRole()
			const canRename = !!role && role !== "VIEWER"
			const hasOtherPortals = (workspace?.portalInstances ?? []).length >= 1
			const { tabs: docsTabs } = useDocsTabs()
			const currentTabId = groupTabId(group, docsTabs)
			const moveToTab = async (tabId: string | null) => {
				try {
					await mutateOk(
						zero.mutate(
							mutators.helpCenterTabs.moveGroup({ groupId: group.id, tabId }),
						),
						"server",
					)
				} catch (error) {
					toast.error({
						title: "Couldn't move the group",
						description: error instanceof Error ? error.message : undefined,
					})
				}
			}
			const navigate = useNavigate()
			const { onItemSelected, selectedLanguage } = useHelpCenterNav()
			const params = useParams({ strict: false })

			const translatedGroupName = translatedName(group, selectedLanguage)
			const renderArticles = useDelayedUnmount(isOpen, 250)

			const commitName = async (name: string) => {
				setIsEditingName(false)
				if (!workspace || name === translatedGroupName) return
				if (!name) {
					toast.info({ title: "A group needs a name" })
					return
				}
				try {
					await renameGroup({
						zero,
						workspaceId: workspace.id,
						defaultLanguage: workspace.defaultLanguage ?? "en",
						group,
						language: selectedLanguage.code,
						name,
					})
					analytics.track("rename-helpcenter-group", {
						groupName: name,
						language: selectedLanguage.code,
						inline: true,
					})
				} catch (error) {
					toast.error({
						title: "Could not rename the group",
						description:
							error instanceof Error
								? error.message
								: "Please try again in a moment.",
					})
				}
			}

			const draftCount = useMemo(() => {
				return group.helpCenterArticles.filter((a) => !a.published).length
			}, [group.helpCenterArticles])

			const redirects = useMemo(
				() => folderRedirects(group.helpCenterArticles),
				[group.helpCenterArticles],
			)

			const articleIds = useMemo(
				() => group.helpCenterArticles.map((a) => a.id),
				[group.helpCenterArticles],
			)

			const sortableData = useMemo(
				() => ({ type: "group" as const, group }),
				[group],
			)

			const {
				attributes,
				listeners,
				setNodeRef,
				transform,
				transition,
				isDragging,
			} = useSortable({
				id: group.id,
				data: sortableData,
			})

			const style = {
				// Translate only: CSS.Transform includes a scale for differently
				// sized rows, which visibly distorts the text mid-drag.
				transform: CSS.Translate.toString(transform),
				transition,
				opacity: isDragging ? 0.4 : undefined,
			}
			const dropSide = useDropSignal(group.id)
			const headerDropData = useMemo<GroupHeaderDropTarget>(
				() => ({ type: "group-header", group }),
				[group],
			)
			const { setNodeRef: setHeaderNodeRef } = useDroppable({
				id: groupHeaderDropId(group.id),
				data: headerDropData,
			})
			const headerDropSide = useDropSignal(groupHeaderDropId(group.id))

			return (
				<>
					<AlertDialog
						isOpen={isDeleteOpen}
						onOpenChange={setIsDeleteOpen}
						confirmText="Delete"
						title={`Delete "${translatedGroupName}"?`}
						description={`Are you sure you want to delete "${translatedGroupName}"? This will permanently delete the group and ${group.helpCenterArticles.length} article${group.helpCenterArticles.length === 1 ? "" : "s"} within it.`}
						onCancel={() => {
							analytics.track("cancel-delete-helpcentergroup", {
								id: group.id,
							})
							setIsDeleteOpen(false)
						}}
						onConfirm={async () => {
							await mutateOk(
								zero.mutate(
									mutators.helpCenterGroups.delete({
										id: group.id,
									}),
								),
							)
							analytics.track("helpcentergroup-delete", {
								id: group.id,
								articleCount: group.helpCenterArticles.length,
							})
							setIsDeleteOpen(false)
							void navigate({ to: "/edit-docs" })
						}}
					/>
					{isMoveOpen && (
						<MoveToPortalDialog
							isOpen={isMoveOpen}
							onClose={() => setIsMoveOpen(false)}
							target={{
								kind: "group",
								id: group.id,
								name: translatedGroupName,
								articleCount: group.helpCenterArticles.length,
								portalInstanceId: group.portalInstanceId ?? null,
							}}
						/>
					)}
					<RenameGroupDialog
						group={group}
						selectedLanguage={selectedLanguage}
						isOpen={isRenameDialogOpen}
						onClose={() => setIsRenameDialogOpen(false)}
					/>
					{isRedirectsOpen && (
						<FolderRedirectsDialog
							groupName={translatedGroupName}
							redirects={redirects}
							isOpen={isRedirectsOpen}
							onClose={() => setIsRedirectsOpen(false)}
						/>
					)}
					<div
						ref={(el) => {
							setNodeRef(el)
							if (typeof ref === "function") ref(el)
							else if (ref) ref.current = el
						}}
						style={style}
						className="relative"
						{...attributes}
					>
						{(dropSide === "before" || headerDropSide === "before") && (
							<div className="pointer-events-none absolute inset-x-1 -top-0.5 z-20 h-0.5 rounded-full bg-black dark:bg-white" />
						)}
						{dropSide === "after" && (
							<div className="pointer-events-none absolute inset-x-1 -bottom-0.5 z-20 h-0.5 rounded-full bg-black dark:bg-white" />
						)}
						<div
							ref={setHeaderNodeRef}
							role="button"
							tabIndex={0}
							onClick={(e) => {
								e.preventDefault()
								// The second click of a double-click renames instead of
								// toggling the group back.
								if (canRename && e.detail > 1) return
								toggleGroup(group.id)
							}}
							onDoubleClick={() => {
								if (canRename) setIsEditingName(true)
							}}
							onKeyDown={(e) => {
								if (e.key === "Enter" || e.key === " ") {
									e.preventDefault()
									toggleGroup(group.id)
								}
							}}
							{...listeners}
							className={cn(
								"group/item group relative flex h-[1.9rem] w-full cursor-default select-none items-center justify-between rounded-md bg-transparent pl-2 pr-1 text-sm hover:bg-gray-100 hover:text-primary hover:dark:bg-gray-800/50",
								isEditingName && "bg-gray-100 dark:bg-gray-800/50",
							)}
						>
							{headerDropSide === "after" && (
								<div className="pointer-events-none absolute inset-x-1 -bottom-0.5 z-20 h-0.5 rounded-full bg-black dark:bg-white" />
							)}
							<div className="min-w-0 flex-1">
								{isEditingName ? (
									<GroupNameInput
										initialValue={translatedGroupName}
										onCommit={(name) => void commitName(name)}
										onCancel={() => setIsEditingName(false)}
									/>
								) : (
									<div className="truncate text-left font-medium text-secondary opacity-90 group-hover:text-primary">
										{translatedGroupName}
									</div>
								)}
							</div>
							<div className="flex flex-none items-center gap-1">
								{draftCount > 0 && (
									<Badge className="pointer-events-none flex select-none flex-row items-center border-none bg-gray-100 py-0.5 text-[0.65rem] font-normal text-gray-600 opacity-100 ring-0 group-hover:opacity-0 dark:bg-gray-800 dark:text-gray-400">
										{draftCount > 99 ? "99+" : draftCount}
									</Badge>
								)}
								<NavDisclosureChevron isOpen={isOpen} />
							</div>
							{/* 28px = pr-1 + the chevron's 24px box, so the strip meets the
							chevron and the name has no gap to show through. */}
							{canRename && !isEditingName && (
								<NavRowActions className="right-7">
									<DropdownMenu
										className="w-60"
										contentProps={{
											align: "end",
											alignOffset: -56,
										}}
										trigger={
											<IconButton
												Icon={Dots}
												variant="ghost"
												size="md"
												iconStrokeWidth={1.75}
												iconClassName="h-3 w-3"
												className="relative text-gray-500 hover:bg-gray-200 dark:text-gray-400 dark:hover:bg-gray-700"
												type="button"
												onClick={(e) => {
													e.preventDefault()
												}}
											/>
										}
									>
										<DropdownMenuItem
											Icon={PenIcon}
											onClick={() => {
												setIsRenameDialogOpen(true)
											}}
										>
											Edit
										</DropdownMenuItem>
										<DropdownMenuItem
											Icon={RedirectIcon}
											onClick={() => {
												setIsRedirectsOpen(true)
											}}
										>
											Redirects
											{redirects.length > 0 && (
												<Badge className="ml-auto">{redirects.length}</Badge>
											)}
										</DropdownMenuItem>
										{hasOtherPortals && (
											<DropdownMenuItem
												Icon={MoveToPortal}
												onClick={() => {
													setIsMoveOpen(true)
												}}
											>
												Move to portal
											</DropdownMenuItem>
										)}
										{docsTabs.length > 0 && (
											<DropdownMenuSub>
												<DropdownMenuSubTrigger className="gap-3 px-2 text-secondary">
													<TabsIcon className="h-4 w-4 flex-none" />
													Move to tab
												</DropdownMenuSubTrigger>
												<DropdownMenuPortal>
													<DropdownMenuSubContent>
														{[
															{
																id: null,
																name: "Documentation",
															},
															...docsTabs,
														].map((tab) => (
															<DropdownMenuItem
																key={tab.id ?? "default"}
																Icon={
																	currentTabId === tab.id ? Check : undefined
																}
																disabled={currentTabId === tab.id}
																onClick={() => void moveToTab(tab.id)}
															>
																{tab.name}
															</DropdownMenuItem>
														))}
													</DropdownMenuSubContent>
												</DropdownMenuPortal>
											</DropdownMenuSub>
										)}
										<DropdownMenuItem
											Icon={Trash}
											onClick={() => {
												setIsDeleteOpen(true)
											}}
										>
											Delete
										</DropdownMenuItem>
									</DropdownMenu>

									<Tooltip label="Add article">
										<IconButton
											variant="ghost"
											size="md"
											className="relative text-gray-500 hover:bg-gray-200 dark:text-gray-400 dark:hover:bg-gray-700"
											type="button"
											Icon={Plus}
											iconStrokeWidth={1.75}
											iconClassName="h-3 w-3"
											onClick={async (e) => {
												e.preventDefault()
												e.stopPropagation()

												const id = generateId()
												await mutateOk(
													zero.mutate(
														mutators.helpCenterArticles.create({
															id,
															title: "",
															helpCenterGroupId: group.id,
															parentArticleId: null,
															order: group.helpCenterArticles.length,
															archived: false,
															content: {},
															date: Date.now(),
															icon: "Page2",
															imageBlurhash: null,
															imageUrl: "",
															published: false,
															showOnHomePage: false,
															summary: "",
															urlName: "",
															portalInstanceId: getActivePortalInstanceId(),
														}),
													),
												)
												setTimeout(() => {
													openGroup(group.id)
													void navigate({
														to: "/edit-docs/$id",
														params: { id },
													})
													onItemSelected()
												}, 200)
											}}
										></IconButton>
									</Tooltip>
								</NavRowActions>
							)}
						</div>

						<div
							inert={!isOpen}
							className={cn(
								"ease-[cubic-bezier(0.23,1,0.32,1)] grid transition-[grid-template-rows,opacity] duration-200",
								isOpen
									? "grid-rows-[1fr] opacity-100"
									: "grid-rows-[0fr] opacity-40",
							)}
						>
							<div className="min-h-0 overflow-hidden">
								<ul className="list-none space-y-0.5 pb-3 pt-1 text-secondary">
									{renderArticles && (
										<SortableContext items={articleIds} strategy={noSortShift}>
											{group.helpCenterArticles.map((item) => (
												<HelpCenterNavItem
													key={item.id}
													article={item}
													groupId={group.id}
													sortableDisabled={!isOpen}
													ref={
														item.id === params.id ? selectedItemRef : undefined
													}
												/>
											))}
										</SortableContext>
									)}
								</ul>
							</div>
						</div>
					</div>
				</>
			)
		},
	),
)
